import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";

type PriceTableRow = { id: string; name: string; adjustmentPercent: number; partnerCount: number };
type PartnerRow = { partnerId: string; clientOrganizationId: string; name: string; priceTableId: string | null };

type Props = {
  table: PriceTableRow;
  partners: PartnerRow[];
  locked: boolean;
  onDeleted: () => void;
};

/** Gestão da tabela de preço selecionada: renomear e excluir a tabela, e vincular/desvincular
 *  clínicas parceiras a ela — mostrado só quando o escopo de PriceListSection é uma tabela. */
export function PriceTableManagePanel({ table, partners, locked, onDeleted }: Props) {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const [name, setName] = useState(table.name);
  const [addPartnerId, setAddPartnerId] = useState("");

  const invalidatePartners = () => {
    utils.labPricing.clientPricing.listPartners.invalidate();
    utils.labPricing.priceTables.list.invalidate();
  };

  const rename = trpc.labPricing.priceTables.rename.useMutation({
    onSuccess: () => {
      utils.labPricing.priceTables.list.invalidate();
      toast.success("Nome atualizado.");
    },
    onError: (err) => toast.error(err.message || "Erro ao renomear tabela."),
  });

  const remove = trpc.labPricing.priceTables.delete.useMutation({
    onSuccess: () => {
      invalidatePartners();
      toast.success("Tabela excluída.");
      onDeleted();
    },
    onError: (err) => toast.error(err.message || "Erro ao excluir tabela."),
  });

  const setPriceTable = trpc.partner.setPriceTable.useMutation({
    onSuccess: invalidatePartners,
    onError: (err) => toast.error(err.message || "Erro ao atualizar vínculo."),
  });

  const handleRenameBlur = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== table.name) rename.mutate({ id: table.id, name: trimmed });
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Excluir "${table.name}"?`,
      description:
        table.partnerCount > 0
          ? `${table.partnerCount} clínica(s) vinculada(s) voltam automaticamente pra config individual. Essa ação não pode ser desfeita.`
          : "Essa ação não pode ser desfeita.",
      confirmText: "Excluir",
      destructive: true,
    });
    if (ok) remove.mutate({ id: table.id });
  };

  const handleAdd = () => {
    if (!addPartnerId) return;
    setPriceTable.mutate({ id: addPartnerId, priceTableId: table.id });
    setAddPartnerId("");
  };

  const linked = partners.filter((p) => p.priceTableId === table.id);
  const unlinked = partners.filter((p) => !p.priceTableId);

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-zinc-200 bg-zinc-50/50 p-3 dark:border-border dark:bg-background/40">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[12.5px] text-zinc-600 dark:text-muted-foreground">Nome:</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleRenameBlur}
            disabled={locked}
            className="h-8 w-48 rounded-md border border-zinc-200 bg-white px-2 text-[13px] text-zinc-900 disabled:opacity-60 dark:border-border dark:bg-card dark:text-foreground"
          />
        </div>
        {!locked && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={remove.isPending}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-red-200 px-2.5 text-[12.5px] font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Excluir tabela
          </button>
        )}
      </div>

      <div>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
          Clínicas vinculadas ({linked.length})
        </p>
        {linked.length === 0 ? (
          <p className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            Nenhuma clínica vinculada ainda.
          </p>
        ) : (
          <ul className="space-y-1">
            {linked.map((p) => (
              <li
                key={p.partnerId}
                className="flex items-center justify-between gap-2 rounded-md bg-white px-2.5 py-1.5 text-[13px] text-zinc-800 dark:bg-card dark:text-foreground"
              >
                {p.name}
                {!locked && (
                  <button
                    type="button"
                    onClick={() => setPriceTable.mutate({ id: p.partnerId, priceTableId: null })}
                    className="text-[12px] font-medium text-zinc-500 hover:text-red-600 hover:underline dark:text-muted-foreground"
                  >
                    Remover
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {!locked && unlinked.length > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <select
              value={addPartnerId}
              onChange={(e) => setAddPartnerId(e.target.value)}
              className="h-8 flex-1 rounded-md border border-zinc-200 bg-white px-2 text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
            >
              <option value="">Adicionar clínica...</option>
              {unlinked.map((p) => (
                <option key={p.clientOrganizationId} value={p.partnerId}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!addPartnerId || setPriceTable.isPending}
              className="inline-flex h-8 items-center rounded-md bg-brand-600 px-3 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              Adicionar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
